import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, Download, Square, Info, Volume2, VolumeX, Link2, Check, Send, Maximize2, Minimize2 } from 'lucide-react';
import {
  MockSpringboard, MockFinance, MockCockpit, MockStocks, MockBaseCamp, MockPlanning, MockPointage,
  MockRecettes, MockUber, MockTasks, MockTaskExample, MockTeam, MockSocial, MockTrends, MockHaccp, MockAgents,
  AgentSuggestionBubble, AgentPresenter,
} from './AuditRestoMockups';

/**
 * AppDemoVideo v5 — "De cuistot à PDG" · 90s · Premium SaaS cinematic
 *
 * Style: Linear / Stripe / Apple Pro
 * - NO phone frame · NO avocado · NO joujou
 * - Dark cinematic with warm orange ambient
 * - Floating dark panels (FloatingPanel)
 * - Kinetic typography scenes between modules
 * - Smooth crossfade transitions
 */

const DURATION = 156.0;

// Scene timings PERFECTLY mapped to Sabril's actual voice recording
// (verified phrase-by-phrase against Whisper transcript).
const SCENES = [
  { id: 'hook',         start: 0.00,    end: 2.00,   act: 1 },   // "Tu contrôles rien."
  { id: 'kinetic1',     start: 2.00,    end: 7.00,   act: 1 },   // "Les marges, les tâches..."
  { id: 'pivot',        start: 7.00,    end: 10.00,  act: 1 },   // "Et si l'application..."
  { id: 'logo',         start: 10.00,   end: 13.00,  act: 1 },   // "Audit Resto..."
  { id: 'springboard',  start: 13.00,   end: 15.00,  act: 2 },   // "28 modules..."
  { id: 'cockpit',      start: 15.00,   end: 25.70,  act: 2 },   // Cockpit + "287€" reply
  { id: 'finance',      start: 25.70,   end: 29.70,  act: 2 },   // "Les marges calculées... Théo"
  { id: 'stocks',       start: 29.70,   end: 33.70,  act: 2 },   // "Zéro casse... Manu croise"
  { id: 'kinetic2',     start: 33.70,   end: 37.70,  act: 2 },   // "Plus de vols, casses..."
  { id: 'tasks',        start: 37.70,   end: 42.70,  act: 2 },   // "L'app rappelle..."
  { id: 'task_example', start: 42.70,   end: 53.70,  act: 2 },   // Karim + reply
  { id: 'planning',     start: 53.60,   end: 59.70,  act: 2 },   // "Sophie adapte..."
  { id: 'pointage',     start: 59.70,   end: 63.70,  act: 2 },   // "Heures réelles..."
  { id: 'team',         start: 63.70,   end: 67.70,  act: 2 },   // "Classement live..."
  { id: 'uber',         start: 67.70,   end: 80.60,  act: 2 },   // Uber + reply "+25€"
  { id: 'recettes',     start: 80.60,   end: 86.70,  act: 2 },   // "Léo, ton chef IA..."
  { id: 'basecamp',     start: 86.70,   end: 91.70,  act: 2 },   // "Basecamp..."
  { id: 'haccp',        start: 91.70,   end: 97.60,  act: 2 },   // "Conformité 100%..."
  { id: 'social',       start: 97.60,   end: 110.70, act: 2 },   // Clara + reply "2400 vues"
  { id: 'trends',       start: 110.70,  end: 114.70, act: 2 },   // "Tendance 2026..."
  { id: 'agents',       start: 114.60,  end: 121.70, act: 2 },   // "Léo, Sophie..."
  { id: 'kinetic4',     start: 121.70,  end: 126.70, act: 2 },   // "28 modules. On contrôle..."
  { id: 'philosophie',  start: 126.70,  end: 137.70, act: 3 },   // "Plus de favoritisme..."
  { id: 'roi',          start: 137.70,  end: 142.70, act: 4 },   // "Ton gain net 3400€..."
  { id: 'payoff',       start: 142.70,  end: 149.70, act: 4 },   // "De cuistot à PDG..."
  { id: 'cta',          start: 149.70,  end: 156.00, act: 4 },   // "229€/mois..."
];

const fadeScale = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.02 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// ============================== Counter ==============================
const Counter = ({ from, to, duration = 2, prefix = '', suffix = '', className }) => {
  const [val, setVal] = useState(from);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span className={className}>{prefix}{val.toLocaleString('fr-FR')}{suffix}</span>;
};

// ============================== Kinetic word reveal ==============================
const KineticWords = ({ words, color = '#fff', delay = 0, size = 'clamp(48px, 9vw, 110px)' }) => (
  <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
    {words.map((w, i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, y: 28, filter: 'blur(10px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ delay: delay + i * 0.22, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="font-bebas tracking-tight leading-[0.95]"
        style={{ color, fontSize: size }}
      >
        {w}
      </motion.span>
    ))}
  </div>
);

// ============================== Cinematic light sweep ==============================
// Sweeps a soft white diagonal flash across its parent panel — Apple-style polish.
const LightSweep = ({ delay = 0.6, duration = 1.6 }) => (
  <motion.div
    className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 0] }}
    transition={{ delay, duration, ease: 'easeInOut' }}
  >
    <motion.div
      className="absolute -inset-y-10 w-1/2"
      initial={{ x: '-120%', skewX: -20 }}
      animate={{ x: '220%', skewX: -20 }}
      transition={{ delay, duration, ease: 'easeInOut' }}
      style={{
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
        filter: 'blur(6px)',
      }}
    />
  </motion.div>
);

// ============================== ACTE I ==============================

const SceneHook = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex items-center justify-center px-6">
    <div className="text-center">
      <motion.p
        initial={{ opacity: 0, y: -10, letterSpacing: '0.5em' }}
        animate={{ opacity: 1, y: 0, letterSpacing: '0.25em' }}
        transition={{ delay: 0.05, duration: 0.5 }}
        className="text-white/55 text-sm sm:text-base uppercase font-bold mb-4"
      >
        Restaurateur ?
      </motion.p>
      <motion.div
        initial={{ opacity: 0, scale: 1.4, filter: 'blur(18px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ delay: 0.35, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="font-bebas tracking-tight leading-[0.92] text-white"
        style={{ fontSize: 'clamp(60px, 11vw, 130px)' }}
      >
        TU CONTRÔLES
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 1.6, filter: 'blur(22px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ delay: 0.95, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="font-bebas tracking-tight leading-[0.92] text-red-500 mt-2"
        style={{ fontSize: 'clamp(80px, 15vw, 180px)', textShadow: '0 0 60px rgba(239,68,68,0.5)' }}
      >
        RIEN.
      </motion.div>
    </div>
  </motion.div>
);

const SceneKinetic1 = () => {
  // 5 modules accumulating to convey restaurateur overwhelm
  // Matches voice: "Les marges, les tâches, les plannings, l'équipe, les livraisons."
  // Spread over ~5s → 1s per word
  const items = [
    { label: 'Marges',     color: '#22C55E', glow: 'rgba(34,197,94,0.45)' },
    { label: 'Tâches',     color: '#F59E0B', glow: 'rgba(245,158,11,0.45)' },
    { label: 'Plannings',  color: '#EF4444', glow: 'rgba(239,68,68,0.45)' },
    { label: 'Équipe',     color: '#A78BFA', glow: 'rgba(167,139,250,0.45)' },
    { label: 'Livraisons', color: '#EC4899', glow: 'rgba(236,72,153,0.45)' },
  ];
  return (
    <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6 gap-3">
      <motion.p
        initial={{ opacity: 0, letterSpacing: '0.6em' }}
        animate={{ opacity: 1, letterSpacing: '0.28em' }}
        transition={{ duration: 0.5 }}
        className="text-white/45 text-xs sm:text-sm uppercase font-bold mb-2"
      >
        Tu dois tout gérer
      </motion.p>
      <div className="flex flex-col items-center gap-2.5">
        {items.map((it, i) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, x: i % 2 === 0 ? -80 : 80, scale: 0.8, filter: 'blur(8px)' }}
            animate={{
              opacity: 1, x: 0, scale: 1, filter: 'blur(0px)',
            }}
            transition={{ delay: 0.15 + i * 0.95, type: 'spring', stiffness: 220, damping: 16 }}
            className="px-5 py-2 rounded-2xl border backdrop-blur-md flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, ${it.color}22, ${it.color}10)`,
              borderColor: `${it.color}55`,
              boxShadow: `0 0 30px ${it.glow}`,
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: it.color, boxShadow: `0 0 10px ${it.color}` }}
            />
            <span
              className="font-bebas tracking-tight text-white"
              style={{ fontSize: 'clamp(28px, 4.8vw, 52px)' }}
            >
              {it.label}.
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const ScenePivot = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6">
    <motion.p
      initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ delay: 0.3, duration: 0.8 }}
      className="font-bebas text-white text-center leading-[0.92]"
      style={{ fontSize: 'clamp(40px, 7vw, 84px)' }}
    >
      ET SI L'APP
    </motion.p>
    <motion.p
      initial={{ opacity: 0, scale: 0.5, filter: 'blur(18px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="font-bebas text-center leading-[0.92] mt-2"
      style={{ fontSize: 'clamp(56px, 11vw, 130px)', color: '#F05A1A', textShadow: '0 0 60px rgba(240,90,26,0.5)' }}
    >
      FAISAIT LE JOB ?
    </motion.p>
    {/* Pulse glow underline */}
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: ['0%', '40%', '40%'], opacity: [0, 1, 0.6] }}
      transition={{ delay: 2.2, duration: 1.2 }}
      className="h-[2px] mt-4"
      style={{ background: 'linear-gradient(90deg, transparent, #F05A1A, transparent)', boxShadow: '0 0 16px #F05A1A' }}
    />
  </motion.div>
);

const SceneLogo = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center">
    <motion.div
      initial={{ scale: 0, rotate: -25, opacity: 0 }}
      animate={{ scale: 1, rotate: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 140, damping: 14, delay: 0.2 }}
      className="rounded-3xl px-8 py-5"
      style={{ background: '#F05A1A', boxShadow: '0 0 120px rgba(240,90,26,0.65)' }}
    >
      <motion.p
        animate={{ scale: [1, 1.02, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        className="font-bebas tracking-tight text-white text-5xl sm:text-7xl"
      >
        AUDIT RESTO
      </motion.p>
    </motion.div>
    <motion.p
      initial={{ opacity: 0, y: 14, letterSpacing: '0.8em' }}
      animate={{ opacity: 1, y: 0, letterSpacing: '0.25em' }}
      transition={{ delay: 1.2, duration: 0.9 }}
      className="text-white/80 text-base sm:text-2xl mt-6 font-light uppercase"
    >
      L'<span className="text-[#F05A1A] font-bold">OS</span> de ton resto.
    </motion.p>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: '50%' }}
      transition={{ delay: 1.7, duration: 1.6, ease: 'easeOut' }}
      className="h-[1px] bg-gradient-to-r from-transparent via-orange-500 to-transparent mt-4"
      style={{ boxShadow: '0 0 16px #F05A1A' }}
    />
  </motion.div>
);

// ============================== ACTE II — Phone-less feature scenes ==============================
// Layout: caption left, floating panel right

const SceneKinetic2 = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6">
    <motion.p
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2, duration: 0.7 }}
      className="font-bebas text-white/40 text-2xl sm:text-3xl uppercase tracking-widest mb-3"
    >
      Plus de
    </motion.p>
    {['VOL.', 'CASSE.', 'OUBLI.', 'PERTE.'].map((w, i) => (
      <motion.p
        key={w}
        initial={{ opacity: 0, scale: 0.4, filter: 'blur(10px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ delay: 0.5 + i * 0.55, type: 'spring', stiffness: 180, damping: 12 }}
        className="font-bebas text-red-500/90 leading-tight"
        style={{ fontSize: 'clamp(48px, 9vw, 100px)', textShadow: '0 0 40px rgba(239,68,68,0.4)' }}
      >
        ✗ {w}
      </motion.p>
    ))}
  </motion.div>
);

const SceneKinetic3 = () => null;

const SceneKinetic4 = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6 gap-2">
    <motion.p
      initial={{ opacity: 0, letterSpacing: '0.8em' }}
      animate={{ opacity: 1, letterSpacing: '0.31em' }}
      transition={{ duration: 0.9, ease: 'easeOut' }}
      className="text-white/50 text-xs sm:text-sm font-bold uppercase"
    >
      28 modules
    </motion.p>
    <motion.p
      initial={{ opacity: 0, scale: 0.8, filter: 'blur(8px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ delay: 0.6, duration: 0.8 }}
      className="font-bebas text-white leading-[0.95] text-center"
      style={{ fontSize: 'clamp(40px, 7vw, 76px)' }}
    >
      ON CONTRÔLE
    </motion.p>
    {['les coûts.', 'les équipes.', 'les marges.', 'la conformité.'].map((w, i) => (
      <motion.p
        key={w}
        initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60, filter: 'blur(8px)' }}
        animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
        transition={{ delay: 1.3 + i * 0.35, duration: 0.6 }}
        className="font-bebas tracking-tight text-white/85"
        style={{ fontSize: 'clamp(28px, 5vw, 56px)' }}
      >
        {w}
      </motion.p>
    ))}
  </motion.div>
);

// ============================== Phone-less feature scenes ==============================
// Layout: caption left, floating panel right

// ============================== Floating accent particles ==============================
// Tiny luminous particles that drift around feature panels — adds "film" depth.
const FloatingParticles = ({ accent, count = 8 }) => {
  const particles = Array.from({ length: count }, (_, i) => i);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible" style={{ zIndex: 1 }}>
      {particles.map((i) => {
        const size = 3 + Math.random() * 4;
        const startX = (Math.random() - 0.5) * 120;
        const startY = (Math.random() - 0.5) * 120;
        const driftX = (Math.random() - 0.5) * 40;
        const driftY = -20 - Math.random() * 60;
        const delay = i * 0.4;
        const duration = 6 + Math.random() * 4;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{
              width: size,
              height: size,
              left: '50%',
              top: '50%',
              background: accent,
              boxShadow: `0 0 ${size * 2}px ${accent}`,
            }}
            initial={{ x: startX, y: startY, opacity: 0 }}
            animate={{
              x: [startX, startX + driftX],
              y: [startY, startY + driftY],
              opacity: [0, 0.85, 0],
            }}
            transition={{ delay, duration, repeat: Infinity, ease: 'easeOut' }}
          />
        );
      })}
    </div>
  );
};

const FeatureScene = ({ kicker, title, sub, accent = '#F05A1A', bubble = null, presenter = null, children }) => (
  <motion.div {...fadeScale} className="absolute inset-0 flex items-center justify-center px-3 sm:px-10 py-0 sm:py-4 overflow-hidden">
    <div
      className="flex flex-col lg:flex-row items-center justify-center gap-0.5 lg:gap-12 w-full max-w-6xl scale-[0.58] sm:scale-100 origin-center"
    >
      {/* Caption */}
      <div className="flex-1 text-center lg:text-left max-w-md order-2 lg:order-1">
        <motion.p
          initial={{ opacity: 0, x: -14, letterSpacing: '0.5em' }}
          animate={{ opacity: 1, x: 0, letterSpacing: '0.3em' }}
          transition={{ delay: 0.05, duration: 0.45 }}
          className="text-[9px] sm:text-xs font-bold uppercase mb-1 sm:mb-3"
          style={{ color: accent }}
        >
          {kicker}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 22, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="font-bebas tracking-tight text-white leading-[0.95]"
          style={{ fontSize: 'clamp(24px, 4.5vw, 64px)' }}
        >
          {title.split('|').map((line, i) => (
            <span key={i}>
              {line}
              {i < title.split('|').length - 1 && <br />}
            </span>
          ))}
        </motion.p>
        {sub && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="hidden sm:block text-white/55 text-sm sm:text-base italic mt-3 leading-relaxed"
            style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
          >
            {sub}
          </motion.p>
        )}
        {/* Subtle bottom underline that draws in — hidden on mobile to save space */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.7, duration: 1.4, ease: 'easeOut' }}
          className="hidden sm:block mt-4 h-[1px] origin-left mx-auto lg:mx-0"
          style={{
            width: '40%',
            background: `linear-gradient(90deg, ${accent}, transparent)`,
            boxShadow: `0 0 12px ${accent}80`,
          }}
        />
        {/* Inline AGENT PRESENTER — hidden on mobile (too vertical, breaks framing) */}
        {presenter && (
          <div className="hidden sm:block">
            <AgentPresenter {...presenter} mode="inline" />
          </div>
        )}
      </div>
      {/* Floating panel · Continuous cinematic motion + parallax + bubble */}
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.88 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          opacity: { delay: 0.4, duration: 0.6 },
          y: { delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
          scale: { delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
        }}
        className="flex-shrink-0 order-1 lg:order-2 relative max-w-full"
        style={{ filter: `drop-shadow(0 30px 60px ${accent}33)`, transformStyle: 'preserve-3d' }}
      >
        <motion.div
          animate={{
            scale: [1, 1.03, 1, 1.025, 1],
            x: [0, -5, 0, 5, 0],
            y: [0, -4, 0, 3, 0],
            rotateZ: [0, 0.35, 0, -0.35, 0],
          }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', times: [0, 0.25, 0.5, 0.75, 1] }}
        >
          <FloatingParticles accent={accent} />
          {children}
          <LightSweep delay={0.9} duration={1.6} />
          <LightSweep delay={4.8} duration={1.5} />
          {bubble && <AgentSuggestionBubble {...bubble} />}
        </motion.div>
      </motion.div>
    </div>
  </motion.div>
);

const SceneSpringboard = () => (
  <FeatureScene
    kicker="28 modules · 1 seule app"
    title="TOUT TON RESTO,|EN 1 ÉCRAN."
    sub="Plus besoin de jongler entre 6 outils. Tout est connecté."
    accent="#06B6D4"
    presenter={{ agentId: 'sabril', text: 'Salut patron 👋 Par quoi tu veux commencer aujourd\'hui ? Je te guide pas à pas.', cta: 'Commencer', ctaIcon: <Send className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockSpringboard />
  </FeatureScene>
);
const SceneCockpit = () => (
  <FeatureScene
    kicker="Cockpit dirigeant · Pilotage industriel"
    title="MANIE TES CHIFFRES|COMME UN PDG."
    sub="Food cost, prime cost, EBE vs charges — tout consolidé. Le patron pilote, l'app calcule."
    accent="#8B5CF6"
    presenter={{
      agentId: 'theo',
      replies: [
        { text: 'EBE +18% ce mois. Pousse le burger avo ce week-end → +320€ estimés. Je l\'active ?', cta: 'Activer', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} /> },
        { text: 'Push activé. +287€ générés sur 2 jours. ROI confirmé.', success: true },
      ],
      size: 110,
    }}
  >
    <MockCockpit />
  </FeatureScene>
);
const SceneFinance = () => (
  <FeatureScene
    kicker="Théo IA · Finance & marges"
    title="LES MARGES,|EN DIRECT."
    sub="EBE, food cost, prime cost — calculés en continu."
    accent="#8B5CF6"
    presenter={{ agentId: 'theo', text: 'Food cost à 27,8% — −2,4 pts vs sept. Je sécurise le rab de pain ?', cta: 'Tout sécuriser', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockFinance />
  </FeatureScene>
);
const SceneStocks = () => (
  <FeatureScene
    kicker="Manu IA · Anti-pertes"
    title="ZÉRO CASSE|NON DÉTECTÉE."
    sub="L'IA croise tes ventes et tes stocks. Le moindre écart, tu le vois."
    accent="#F05A1A"
    presenter={{ agentId: 'manu', text: 'J\'ai détecté 60 produits non liés. Je les rattache maintenant — Food Cost ↗️', cta: 'Tout lier', ctaIcon: <Link2 className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockStocks />
  </FeatureScene>
);
const SceneTasks = () => (
  <FeatureScene
    kicker="Manu IA · Suivi tâches"
    title="L'APP RAPPELLE.|L'APP AVERTIT."
    sub="Tu définis. L'app contrôle. Photo de validation obligatoire."
    accent="#F05A1A"
    presenter={{ agentId: 'manu', text: 'Karim n\'a pas validé le filtre friteuse. Je le relance avec photo obligatoire ?', cta: 'Relancer', ctaIcon: <Send className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockTasks />
  </FeatureScene>
);
const SceneTaskExample = () => (
  <FeatureScene
    kicker="Exemple concret · Escalade IA"
    title="3 RAPPELS IGNORÉS|= AVERTISSEMENT."
    sub="Karim n'a pas sorti les poubelles à 19:30. L'IA escalade toute seule. Sa prime descend en direct."
    accent="#EF4444"
    presenter={{
      agentId: 'manu',
      replies: [
        { text: '3 rappels ignorés sur la même tâche. C\'est un schéma, pas un oubli. Avertissement officiel ?', cta: 'Envoyer', ctaIcon: <Send className="w-3 h-3" strokeWidth={2.8} /> },
        { text: 'Avertissement notifié. Prime de Karim ajustée −30€. Tracé dans son dossier RH.', success: true },
      ],
      size: 110,
    }}
  >
    <MockTaskExample />
  </FeatureScene>
);
const ScenePlanning = () => (
  <FeatureScene
    kicker="Sophie IA · Planning intelligent"
    title="LE PLANNING|S'ADAPTE TOUT SEUL."
    sub="Météo + CA prévi → planning optimisé. Ou manuel si tu veux la main."
    accent="#3B82F6"
    presenter={{ agentId: 'sophie', text: 'Pluie samedi → CA −22%. Je retire Marie du shift midi. Économie : 230€.', cta: 'Valider', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockPlanning />
  </FeatureScene>
);
const ScenePointage = () => (
  <FeatureScene
    kicker="Pointage selfie + polo IA"
    title="HEURES RÉELLES.|PAS FICTIVES."
    sub="Photo + reconnaissance du polo. Plus aucune heure sup' fantôme."
    accent="#3B82F6"
    presenter={{ agentId: 'sophie', text: 'Polo détecté ✓ Pointage validé pour Marie L. à 11:02. Aucune anomalie.', size: 110 }}
  >
    <MockPointage />
  </FeatureScene>
);
const SceneTeam = () => (
  <FeatureScene
    kicker="Sophie IA · Managé aux stats"
    title="CLASSEMENT LIVE.|PRIMES AUTO."
    sub="Coach tacticien — pas l'émotion. 30/30/30/10 chaque mois."
    accent="#3B82F6"
    presenter={{ agentId: 'sophie', text: 'Marie L. atteint 96/100. Prime +200€ débloquée auto. Je la notifie ?', cta: 'Notifier', ctaIcon: <Send className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockTeam />
  </FeatureScene>
);
const SceneUber = () => (
  <FeatureScene
    kicker="🔥 Money shot · Litiges Uber"
    title="L'IA SCANNE.|TU ES REMBOURSÉ."
    sub="Ticket + photo + horodatage = remboursement quasi systématique."
    accent="#F05A1A"
    presenter={{
      agentId: 'manu',
      replies: [
        { text: 'Ticket Uber scanné, preuve photo OK. Litige envoyé chez Uber.', cta: 'Voir le détail', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} /> },
        { text: 'Remboursement Uber accepté : +25€ récupérés. 4ème de ce mois.', success: true },
      ],
      size: 110,
    }}
  >
    <MockUber />
  </FeatureScene>
);
const SceneRecettes = () => (
  <FeatureScene
    kicker="Léo IA · Chef cuisinier"
    title="DU PAPIER|AU FOOD COST."
    sub="Photo de ta fiche manuscrite → recette calibrée au gramme."
    accent="#22C55E"
    presenter={{ agentId: 'leo', text: 'Recette analysée. Food cost 2,84€ — marge 73%. Je l\'ajoute à la carte ?', cta: 'Ajouter', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockRecettes />
  </FeatureScene>
);
const SceneBaseCamp = () => (
  <FeatureScene
    kicker="Base Camp · Montée en compétences"
    title="GAMIFIÉ.|CERTIFIÉ HACCP."
    sub="Parcours d'intégration · badges · certificats officiels."
    accent="#F59E0B"
    presenter={{ agentId: 'sabril', text: 'Karim est à 92% du niveau 5. Lance le quiz HACCP final ?', cta: 'Lancer', ctaIcon: <Send className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockBaseCamp />
  </FeatureScene>
);
const SceneHaccp = () => (
  <FeatureScene
    kicker="Léo IA · HACCP · 0 papier"
    title="COMPLIANCE|PROUVÉE 100%."
    sub="Étiquettes, températures, traçabilité — archivés et exportables."
    accent="#22C55E"
    presenter={{ agentId: 'leo', text: 'Conformité 100%. 12 étiquettes ce mois. T° friteuse 72°C. Audit DDPP exportable en 1 clic.', cta: 'Voir le rapport', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockHaccp />
  </FeatureScene>
);
const SceneSocial = () => (
  <FeatureScene
    kicker="Clara IA · Réseaux sociaux"
    title="CALENDRIER ÉDITO|+ VIDÉOS VIRALES."
    sub="Planning de la semaine généré + scripts + 3 vidéos verticales prêtes à poster."
    accent="#EC4899"
    presenter={{
      agentId: 'clara',
      replies: [
        { text: '3 vidéos virales prêtes (POV burger, cooking, behind the scene). Je programme pour vendredi ?', cta: 'Programmer', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} /> },
        { text: 'Programmé pour ven. 19h, sam. 12h, dim. 11h. Estimé : +2 400 vues, +18 réservations.', success: true },
      ],
      size: 110,
    }}
  >
    <MockSocial />
  </FeatureScene>
);
const SceneTrends = () => (
  <FeatureScene
    kicker="Clara IA · Tendances 2026"
    title="LE MENU GAGNANT,|SELON TA ZONE."
    sub="Tendances locales en direct. Toujours 6 mois en avance."
    accent="#EC4899"
    presenter={{ agentId: 'clara', text: 'Wraps healthy +34% dans ta zone ce mois. Ajoute le Wrap Avo-Saumon → ticket moyen +12%.', cta: 'Tester', ctaIcon: <Check className="w-3 h-3" strokeWidth={2.8} />, size: 110 }}
  >
    <MockTrends />
  </FeatureScene>
);
const SceneAgents = () => (
  <FeatureScene
    kicker="5 agents IA + ton coach humain"
    title="LÉO · SOPHIE · THÉO|CLARA · MANU · SABRIL."
    sub="Chacun pilote son domaine. Toi tu pilotes le système — comme un PDG, pas un employé."
    accent="#F05A1A"
  >
    <MockAgents />
  </FeatureScene>
);

// ============================== ACTE III — Philosophie ==============================

const ScenePhilosophie = () => {
  const msgs = [
    'Plus de favoritisme.',
    'Plus de drame RH.',
    'Plus de "j\'ai pas eu le temps".',
    { text: 'Que des stats. Que de la performance.', accent: true },
    'Tes meilleurs restent. Les autres comprennent ou partent.',
  ];
  return (
    <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
      <motion.p
        initial={{ opacity: 0, letterSpacing: '0.7em' }}
        animate={{ opacity: 1, letterSpacing: '0.31em' }}
        transition={{ duration: 1.1 }}
        className="text-xs sm:text-sm font-bold uppercase mb-5"
        style={{ color: '#F05A1A' }}
      >
        ⚡ Le management qui change la vie
      </motion.p>
      <div className="space-y-3 max-w-2xl">
        {msgs.map((m, i) => {
          const isObj = typeof m === 'object';
          const text = isObj ? m.text : m;
          const accent = isObj && m.accent;
          return (
            <motion.p
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40, filter: 'blur(10px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              transition={{ delay: 0.5 + i * 1.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-bebas leading-tight"
              style={{
                fontSize: 'clamp(28px, 5vw, 56px)',
                color: accent ? '#22C55E' : '#fff',
                textShadow: accent ? '0 0 40px rgba(34,197,94,0.4)' : 'none',
              }}
            >
              {text}
            </motion.p>
          );
        })}
      </div>
    </motion.div>
  );
};

// ============================== ACTE IV ==============================

const SceneRoi = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-4">
    <motion.p
      initial={{ opacity: 0, letterSpacing: '0.9em' }}
      animate={{ opacity: 1, letterSpacing: '0.31em' }}
      transition={{ duration: 1 }}
      className="text-white/55 text-xs sm:text-sm uppercase mb-4"
    >
      Ton gain net mensuel
    </motion.p>
    <motion.div
      initial={{ scale: 0.4, opacity: 0, filter: 'blur(20px)' }}
      animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
      transition={{ type: 'spring', stiffness: 90, damping: 14, delay: 0.5 }}
      className="font-bebas tabular-nums leading-none"
      style={{ fontSize: 'clamp(80px, 17vw, 200px)', color: '#22C55E', textShadow: '0 0 100px rgba(34,197,94,0.55)' }}
    >
      <Counter from={0} to={3400} duration={3.5} prefix="+" suffix="€" />
    </motion.div>
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 0.7 }}
      className="text-white text-xl sm:text-3xl font-bold mt-3 tracking-wide"
    >
      / MOIS. NET.
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 3.8, duration: 0.8 }}
      className="text-white/55 text-sm sm:text-lg mt-5 italic"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      Soit <strong className="text-white">+40 800€/an</strong> qui dormaient dans ton resto
    </motion.p>
  </motion.div>
);

const ScenePayoff = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
    <motion.p
      initial={{ opacity: 0, scale: 0.7, filter: 'blur(15px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="font-bebas text-white leading-[0.9]"
      style={{ fontSize: 'clamp(48px, 9vw, 110px)' }}
    >
      DE CUISTOT
    </motion.p>
    <motion.p
      initial={{ opacity: 0, scale: 0.4, filter: 'blur(20px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="font-bebas leading-[0.9] mt-2"
      style={{ fontSize: 'clamp(80px, 16vw, 180px)', color: '#F05A1A', textShadow: '0 0 80px rgba(240,90,26,0.55)' }}
    >
      À PDG.
    </motion.p>
    <motion.p
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2.3, duration: 0.8 }}
      className="text-white/85 text-sm sm:text-lg mt-7 max-w-xl leading-relaxed"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      Ton resto devient <strong className="text-white">une marque industrielle.<br />Pilotable. Autonome. Scalable.</strong>
    </motion.p>
  </motion.div>
);

const SceneCta = () => (
  <motion.div {...fadeScale} className="absolute inset-0 flex flex-col items-center justify-center px-4">
    <motion.div
      initial={{ scale: 0.6, opacity: 0, filter: 'blur(15px)' }}
      animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
      transition={{ type: 'spring', stiffness: 110, damping: 14, delay: 0.2 }}
      className="rounded-3xl px-7 py-4 mb-4"
      style={{ background: '#F05A1A', boxShadow: '0 0 120px rgba(240,90,26,0.6)' }}
    >
      <motion.p
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        className="font-bebas text-white text-3xl sm:text-5xl"
      >
        229€/MOIS.
      </motion.p>
    </motion.div>
    <motion.p
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1, duration: 0.7 }}
      className="text-white/65 text-xs sm:text-sm italic"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      Pas un coût. Un levier. · Sans engagement.
    </motion.p>
    <motion.p
      initial={{ opacity: 0, y: 14, letterSpacing: '0.8em' }}
      animate={{ opacity: 1, y: 0, letterSpacing: '0.2em' }}
      transition={{ delay: 1.8, duration: 1.1 }}
      className="text-white font-bebas mt-8 text-xl sm:text-2xl"
    >
      audit-resto.fr
    </motion.p>
  </motion.div>
);

// =====================================================================
// MAIN COMPONENT
// =====================================================================

// =====================================================================
// CINEMATIC EVENTS — Big punch moments synced with the voice-over
// =====================================================================
// Each event: { scene, t (relative s from scene start), type, ...props }
// Types: 'numberBurst' | 'cashBurst' | 'shake' | 'flash' | 'logoFlare'

const CINEMATIC_EVENTS = [
  // Hook — punchline "tu contrôles RIEN"
  { scene: 'hook', t: 0.8, type: 'flash', color: '#EF4444', text: 'RIEN.', size: 220 },
  // Logo — brand drop
  { scene: 'logo', t: 0.2, type: 'shake', intensity: 6 },
  { scene: 'logo', t: 0.4, type: 'logoFlare' },
  // Cockpit — "Plus de 287 euros générés" at t=7s (scene starts 15, line at 22)
  { scene: 'cockpit', t: 7.0, type: 'cashBurst', value: '+287€', color: '#22C55E' },
  // Stocks — "Manu croise tes ventes" - emphasize 66 alertes at t=2s
  { scene: 'stocks', t: 2.0, type: 'numberBurst', value: '66', label: 'ALERTES', color: '#EF4444', size: 160 },
  // Task example — "Prime ajustée de moins de 30 euros" at t=7s (scene 43, line 50)
  { scene: 'task_example', t: 7.0, type: 'numberBurst', value: '−30€', label: 'PRIME', color: '#EF4444', size: 160 },
  // Uber — "25 euros récupérés" at t=9s (scene 68, line 77)
  { scene: 'uber', t: 9.0, type: 'cashBurst', value: '+25€', color: '#F05A1A' },
  // HACCP — "Conformité prouvée à 100%" at t=0.5s (scene 92, line 92)
  { scene: 'haccp', t: 0.5, type: 'numberBurst', value: '100%', label: 'CONFORMITÉ', color: '#22C55E', size: 200 },
  // Social — "2400 vues" at t=9s (scene 98, line 107)
  { scene: 'social', t: 9.0, type: 'numberBurst', value: '+2 400', label: 'VUES', color: '#EC4899', size: 170 },
  // ROI — "3400 euros par mois" at t=0.5s (scene 138 = line 138)
  { scene: 'roi', t: 0.5, type: 'cashBurst', value: '', color: '#22C55E', burstCount: 30 },
  // ROI — "soit plus de 40 800 euros par an" at t=3s (scene 138, line 141)
  { scene: 'roi', t: 3.0, type: 'numberBurst', value: '+40 800€', label: 'PAR AN', color: '#22C55E', size: 220 },
  // Payoff — "De cuistot à PDG" at t=0s (scene 143 = line 143)
  { scene: 'payoff', t: 0.3, type: 'shake', intensity: 10 },
  { scene: 'payoff', t: 0.6, type: 'flash', color: '#F05A1A', text: 'PDG.', size: 260 },
  // CTA — "189 euros par mois" at t=0s (scene 150 = line 150)
  { scene: 'cta', t: 0.2, type: 'shake', intensity: 8 },
  { scene: 'cta', t: 0.5, type: 'logoFlare' },
];

// ===== NumberBurst — huge number explodes onto the screen =====
const NumberBurst = ({ value, label, color, size = 160, duration = 1.6 }) => (
  <motion.div
    className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center z-40"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 1, 0] }}
    transition={{ duration, times: [0, 0.15, 0.7, 1], ease: 'easeOut' }}
  >
    {label && (
      <motion.p
        initial={{ opacity: 0, letterSpacing: '1em' }}
        animate={{ opacity: 1, letterSpacing: '0.4em' }}
        transition={{ duration: 0.4 }}
        className="text-xs sm:text-sm font-black uppercase mb-2"
        style={{ color, textShadow: `0 0 20px ${color}` }}
      >
        {label}
      </motion.p>
    )}
    <motion.div
      initial={{ scale: 0.3, filter: 'blur(20px)', rotate: -8 }}
      animate={{ scale: [0.3, 1.15, 1], filter: ['blur(20px)', 'blur(0px)', 'blur(0px)'], rotate: [-8, 2, 0] }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="font-bebas tracking-tight tabular-nums px-6 max-w-full text-center"
      style={{
        color,
        fontSize: `clamp(48px, 30vw, ${size}px)`,
        lineHeight: 0.9,
        textShadow: `0 0 40px ${color}, 0 0 80px ${color}88`,
      }}
    >
      {value}
    </motion.div>
    {/* Radial flash behind */}
    <motion.div
      className="absolute inset-0 pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.45, 0] }}
      transition={{ duration: 0.7 }}
      style={{ background: `radial-gradient(circle at center, ${color}55 0%, transparent 50%)` }}
    />
    {/* Particle ring explosion */}
    {[...Array(16)].map((_, i) => {
      const angle = (i / 16) * Math.PI * 2;
      const distance = 240;
      return (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            width: 6,
            height: 6,
            background: color,
            boxShadow: `0 0 12px ${color}`,
            left: '50%',
            top: '50%',
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [1, 1, 0],
            scale: [1, 0.5, 0.3],
          }}
          transition={{ duration: 1.0, ease: 'easeOut' }}
        />
      );
    })}
  </motion.div>
);

// ===== CashBurst — bills cascading down =====
const CashBurst = ({ value, color, burstCount = 18 }) => (
  <motion.div
    className="absolute inset-0 pointer-events-none z-40"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 1, 0] }}
    transition={{ duration: 2.2, times: [0, 0.1, 0.7, 1] }}
  >
    {/* Big number in center — only when value provided (avoids overlap with scene's own counter) */}
    {value && (
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ scale: [0.3, 1.2, 1], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.0, times: [0, 0.15, 0.7, 1] }}
      >
        <p
          className="font-bebas tabular-nums"
          style={{
            color,
            fontSize: 'clamp(72px, 18vw, 200px)',
            lineHeight: 1,
            textShadow: `0 0 30px ${color}, 0 0 60px ${color}88`,
            padding: '0 8px',
          }}
        >
          {value}
        </p>
      </motion.div>
    )}
    {/* Cash bills falling */}
    {[...Array(burstCount)].map((_, i) => {
      const startX = 30 + Math.random() * 40;       // %
      const driftX = (Math.random() - 0.5) * 30;
      const rotate = (Math.random() - 0.5) * 60;
      const dur = 1.5 + Math.random() * 0.8;
      const delay = Math.random() * 0.5;
      return (
        <motion.div
          key={i}
          className="absolute rounded-sm"
          style={{
            width: 28,
            height: 14,
            background: `linear-gradient(135deg, ${color} 0%, ${color}aa 100%)`,
            border: `1px solid ${color}cc`,
            boxShadow: `0 4px 12px ${color}66`,
            left: `${startX}%`,
            top: '-20px',
          }}
          initial={{ y: 0, opacity: 0, rotate: 0 }}
          animate={{
            y: ['0%', '700%'],
            x: ['0%', `${driftX}%`],
            opacity: [0, 1, 1, 0],
            rotate: [0, rotate * 0.5, rotate],
          }}
          transition={{ duration: dur, delay, ease: 'easeIn' }}
        />
      );
    })}
  </motion.div>
);

// ===== FullscreenFlash — color flash on impact word =====
const FullscreenFlash = ({ color, text, size = 200 }) => (
  <motion.div
    className="absolute inset-0 pointer-events-none flex items-center justify-center z-40"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 0] }}
    transition={{ duration: 0.6, times: [0, 0.2, 1] }}
  >
    {/* Color wash */}
    <div className="absolute inset-0" style={{ background: `radial-gradient(circle, ${color}55 0%, transparent 65%)` }} />
    {text && (
      <motion.p
        initial={{ scale: 0.5, filter: 'blur(20px)' }}
        animate={{ scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="font-bebas tracking-tight relative"
        style={{
          color,
          fontSize: size,
          lineHeight: 0.9,
          textShadow: `0 0 60px ${color}, 0 0 120px ${color}`,
        }}
      >
        {text}
      </motion.p>
    )}
  </motion.div>
);

// ===== LogoFlare — diagonal lens flare =====
const LogoFlare = () => (
  <motion.div
    className="absolute inset-0 pointer-events-none overflow-hidden z-40"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 0] }}
    transition={{ duration: 1.1 }}
  >
    <motion.div
      className="absolute -inset-y-20 w-1/2"
      initial={{ x: '-150%', skewX: -20 }}
      animate={{ x: '250%' }}
      transition={{ duration: 1.0, ease: 'easeInOut' }}
      style={{
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,180,100,0.5) 45%, rgba(255,255,255,0.9) 50%, rgba(255,180,100,0.5) 55%, transparent 100%)',
        filter: 'blur(8px)',
      }}
    />
  </motion.div>
);

// ===== CinematicLayer — orchestrates all event triggers =====
const CinematicLayer = ({ elapsed, activeScene }) => {
  const sceneRelTime = elapsed - activeScene.start;
  // Find events within ±0.15s window (60ms tolerance for re-trigger)
  return (
    <>
      {CINEMATIC_EVENTS.filter((ev) => ev.scene === activeScene.id).map((ev, idx) => {
        const isActive = sceneRelTime >= ev.t && sceneRelTime < ev.t + 2.2;
        if (!isActive) return null;
        const key = `${activeScene.id}-${idx}-${Math.floor(elapsed / 5)}`; // re-key on loop
        if (ev.type === 'numberBurst') {
          return <NumberBurst key={key} value={ev.value} label={ev.label} color={ev.color} size={ev.size} />;
        }
        if (ev.type === 'cashBurst') {
          return <CashBurst key={key} value={ev.value} color={ev.color} burstCount={ev.burstCount || 18} />;
        }
        if (ev.type === 'flash') {
          return <FullscreenFlash key={key} color={ev.color} text={ev.text} size={ev.size} />;
        }
        if (ev.type === 'logoFlare') {
          return <LogoFlare key={key} />;
        }
        return null;
      })}
    </>
  );
};

// ===== Screen shake wrapper — triggers shake on impact moments =====
const useScreenShake = (elapsed, activeScene) => {
  const sceneRelTime = elapsed - activeScene.start;
  const shakeEvent = CINEMATIC_EVENTS.find(
    (ev) =>
      ev.scene === activeScene.id &&
      ev.type === 'shake' &&
      sceneRelTime >= ev.t &&
      sceneRelTime < ev.t + 0.4
  );
  return shakeEvent;
};

const AppDemoVideo = () => {
  const [elapsed, setElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true); // mute by default (autoplay policy)
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportError, setExportError] = useState('');
  // Detect mobile / iOS — screen capture API doesn't work there; we show native-recorder
  // instructions instead.
  const isMobileDevice = typeof window !== 'undefined' && (
    /iPad|iPhone|iPod|Android/.test(navigator.userAgent) ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 640px)').matches)
  );
  const isiOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const lastTickRef = useRef(performance.now());
  const rafRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStartRef = useRef(0);
  const streamRef = useRef(null);

  // ===== AUDIO REFS (voice-over + Web Audio music) =====
  const voiceRef = useRef(null);          // <audio> element
  const audioCtxRef = useRef(null);       // AudioContext
  const musicNodesRef = useRef(null);     // { master, padOscs, subOsc, voiceSource, recordDest }
  const lastSceneRef = useRef(null);      // for one-shot swells on transitions

  // Initialize Web Audio music graph (lazy, once) — ENERGETIC EDM-CINEMATIC LOOP
  const initAudioGraph = useCallback(() => {
    if (audioCtxRef.current) return audioCtxRef.current;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.gain.value = 0;  // ramp up on play
      master.connect(ctx.destination);

      // ---- Voice source from <audio> tag ----
      if (voiceRef.current) {
        try {
          const voiceSource = ctx.createMediaElementSource(voiceRef.current);
          const voiceGain = ctx.createGain();
          voiceGain.gain.value = 1.0;
          voiceSource.connect(voiceGain);
          voiceGain.connect(ctx.destination);
        } catch (_e) { /* already connected on hot reload */ }
      }

      // ---- BPM-driven sequencer ----
      const BPM = 110;
      const beatDur = 60 / BPM;            // seconds per quarter note
      const sixteenth = beatDur / 4;       // 16th note

      // ---- Sub bass drone (rich foundation) — VERY low background ----
      const subOsc = ctx.createOscillator();
      subOsc.type = 'triangle';
      subOsc.frequency.value = 55;          // A1
      const subGain = ctx.createGain();
      subGain.gain.value = 0.030;           // was 0.08 → //3 (subtle warmth)
      subOsc.connect(subGain).connect(master);
      subOsc.start();

      // ---- Energetic pad chord (4-bar progression: Am — F — C — G) ----
      const PROGRESSION = [
        [110, 164.81, 220, 261.63],
        [87.31, 130.81, 174.61, 220],
        [98,    130.81, 196,    261.63],
        [98,    146.83, 196,    246.94],
      ];
      const padGain = ctx.createGain();
      padGain.gain.value = 0.016;           // was 0.045 → //3
      const padFilter = ctx.createBiquadFilter();
      padFilter.type = 'lowpass';
      padFilter.frequency.value = 1400;
      padFilter.Q.value = 0.6;
      padGain.connect(padFilter).connect(master);

      // 4 voices (one per chord tone). They follow the progression by retargeting frequency.
      const padOscs = [0, 1, 2, 3].map((vi) => {
        const o = ctx.createOscillator();
        o.type = vi < 2 ? 'sawtooth' : 'triangle';  // rich harmonics on low voices
        o.frequency.value = PROGRESSION[0][vi];
        const oGain = ctx.createGain();
        oGain.gain.value = 0.22;
        o.connect(oGain).connect(padGain);
        // detune LFO for movement
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 0.25 + Math.random() * 0.3;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = 2.5;
        lfo.connect(lfoGain).connect(o.frequency);
        o.start();
        lfo.start();
        return o;
      });

      // ---- Chord-progression scheduler (4 bars loop = 16 beats = ~8.7s) ----
      const startTime = ctx.currentTime;
      const advanceChord = (i) => {
        const chord = PROGRESSION[i % 4];
        const t = ctx.currentTime;
        padOscs.forEach((o, vi) => {
          o.frequency.setTargetAtTime(chord[vi], t, 0.4);
        });
      };
      // schedule chord changes (every 2 beats)
      let chordIdx = 0;
      const chordTimer = setInterval(() => { advanceChord(chordIdx++); }, beatDur * 2 * 1000);

      // ---- KICK DRUM (deep punchy, 4-on-the-floor on quarters) ----
      const scheduleKick = (t) => {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(150, t);
        o.frequency.exponentialRampToValueAtTime(40, t + 0.15);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.14, t);     // was 0.55 → /4 (BG warmth)
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
        o.connect(g).connect(master);
        o.start(t);
        o.stop(t + 0.32);
      };
      // ---- HI-HAT (offbeat 8ths, crisp) ----
      const scheduleHat = (t, accent = false) => {
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
        const noise = ctx.createBufferSource();
        noise.buffer = buf;
        const hpf = ctx.createBiquadFilter();
        hpf.type = 'highpass';
        hpf.frequency.value = 7500;
        const g = ctx.createGain();
        g.gain.setValueAtTime(accent ? 0.05 : 0.022, t);  // was 0.18 / 0.08 → /3.5
        g.gain.exponentialRampToValueAtTime(0.001, t + (accent ? 0.10 : 0.05));
        noise.connect(hpf).connect(g).connect(master);
        noise.start(t);
        noise.stop(t + 0.12);
      };
      // ---- CLAP (snare-like, on 2 & 4) ----
      const scheduleClap = (t) => {
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.15, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
        const noise = ctx.createBufferSource();
        noise.buffer = buf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 1800;
        bp.Q.value = 0.5;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.06, t);     // was 0.22 → /3.5
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
        noise.connect(bp).connect(g).connect(master);
        noise.start(t);
        noise.stop(t + 0.25);
      };

      // Beat scheduler: 16 beats per bar loop, schedules 2 bars ahead
      let nextBeat = startTime + 0.1;
      let beatNum = 0;
      const lookAhead = 0.25;       // schedule 250ms ahead
      const scheduleTimer = setInterval(() => {
        while (nextBeat < ctx.currentTime + lookAhead) {
          // KICK on every beat (4 on the floor)
          scheduleKick(nextBeat);
          // CLAP on beats 2 & 4
          if (beatNum % 4 === 2) scheduleClap(nextBeat);
          // HI-HAT every 8th (between beats too)
          scheduleHat(nextBeat, beatNum % 4 === 0);
          scheduleHat(nextBeat + beatDur / 2, false);
          nextBeat += beatDur;
          beatNum++;
        }
      }, 50);

      musicNodesRef.current = {
        ctx, master, padGain, subGain, padOscs, padFilter,
        chordTimer, scheduleTimer,
      };
      audioCtxRef.current = ctx;
      return ctx;
    } catch (e) {
      console.warn('AudioContext init failed', e);
      return null;
    }
  }, []);

  // One-shot cinematic swell (used on scene transitions)
  const playSwell = useCallback((peakGain = 0.06, duration = 1.2) => {
    const ctx = audioCtxRef.current;
    const nodes = musicNodesRef.current;
    if (!ctx || !nodes || isMuted) return;
    const now = ctx.currentTime;
    // noise burst
    const buf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length); // decaying noise
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 2400;
    noiseFilter.Q.value = 1.2;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0;
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(peakGain, now + 0.25);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    noise.connect(noiseFilter).connect(noiseGain).connect(nodes.master);
    noise.start(now);
    noise.stop(now + duration);
  }, [isMuted]);

  // One-shot deep impact kick (logo, payoff, cta)
  const playImpact = useCallback(() => {
    const ctx = audioCtxRef.current;
    const nodes = musicNodesRef.current;
    if (!ctx || !nodes || isMuted) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.18, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc.connect(g).connect(nodes.master);
    osc.start(now);
    osc.stop(now + 0.6);
  }, [isMuted]);

  // Ramp master gain on mute/unmute changes
  useEffect(() => {
    const ctx = audioCtxRef.current;
    const nodes = musicNodesRef.current;
    if (!ctx || !nodes) return;
    const target = isMuted ? 0 : 1.0;
    nodes.master.gain.cancelScheduledValues(ctx.currentTime);
    nodes.master.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.4);
    if (voiceRef.current) voiceRef.current.muted = isMuted;
  }, [isMuted]);

  const tick = useCallback(() => {
    const now = performance.now();
    const delta = (now - lastTickRef.current) / 1000;
    lastTickRef.current = now;
    setElapsed((prev) => {
      const next = prev + delta;
      return next >= DURATION ? 0 : next;
    });
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      lastTickRef.current = performance.now();
      rafRef.current = requestAnimationFrame(tick);
    } else if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, tick]);

  const activeScene = SCENES.find((s) => elapsed >= s.start && elapsed < s.end) || SCENES[SCENES.length - 1];
  const progress = (elapsed / DURATION) * 100;

  // ===== AUDIO SYNC: voice-over follows `elapsed` (resync on scene changes) =====
  useEffect(() => {
    const v = voiceRef.current;
    if (!v) return;
    if (isPlaying && !isMuted) {
      try { v.currentTime = Math.min(elapsed, v.duration || DURATION); } catch (_e) { /* ignore */ }
      v.play().catch(() => {});
    } else if (!v.paused) {
      v.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isMuted]);

  // ===== AUDIO CUES + RESYNC on scene transitions =====
  useEffect(() => {
    if (lastSceneRef.current === activeScene.id) return;
    const prev = lastSceneRef.current;
    lastSceneRef.current = activeScene.id;

    // Resync voice with elapsed at every scene change (cheap & reliable)
    const v = voiceRef.current;
    if (v && !isMuted) {
      const drift = Math.abs((v.currentTime || 0) - elapsed);
      if (drift > 0.35) {
        try { v.currentTime = Math.min(elapsed, v.duration || DURATION); } catch (_e) { /* ignore */ }
      }
    }

    if (prev === null || isMuted) return;
    if (['logo', 'payoff', 'cta'].includes(activeScene.id)) playImpact();
    else playSwell(0.04, 1.1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScene.id, isMuted, playImpact, playSwell]);

  const renderScene = (id) => {
    switch (id) {
      case 'hook': return <SceneHook />;
      case 'kinetic1': return <SceneKinetic1 />;
      case 'pivot': return <ScenePivot />;
      case 'logo': return <SceneLogo />;
      case 'springboard': return <SceneSpringboard />;
      case 'cockpit': return <SceneCockpit />;
      case 'finance': return <SceneFinance />;
      case 'stocks': return <SceneStocks />;
      case 'kinetic2': return <SceneKinetic2 />;
      case 'tasks': return <SceneTasks />;
      case 'task_example': return <SceneTaskExample />;
      case 'planning': return <ScenePlanning />;
      case 'pointage': return <ScenePointage />;
      case 'team': return <SceneTeam />;
      case 'kinetic3': return <SceneKinetic3 />;
      case 'uber': return <SceneUber />;
      case 'recettes': return <SceneRecettes />;
      case 'basecamp': return <SceneBaseCamp />;
      case 'haccp': return <SceneHaccp />;
      case 'social': return <SceneSocial />;
      case 'trends': return <SceneTrends />;
      case 'agents': return <SceneAgents />;
      case 'kinetic4': return <SceneKinetic4 />;
      case 'philosophie': return <ScenePhilosophie />;
      case 'roi': return <SceneRoi />;
      case 'payoff': return <ScenePayoff />;
      case 'cta': return <SceneCta />;
      default: return null;
    }
  };

  // Update recording timer
  useEffect(() => {
    if (!isRecording) return;
    const interval = setInterval(() => {
      setRecordingTime((performance.now() - recordingStartRef.current) / 1000);
    }, 100);
    return () => clearInterval(interval);
  }, [isRecording]);

  const cleanupRecording = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingTime(0);
  }, []);

  const startRecording = async () => {
    setExportError('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      setExportError("Ton navigateur ne supporte pas l'export. Utilise Chrome ou Edge récent.");
      return;
    }
    try {
      // Request video + tab audio (Chrome requires user to tick "Share tab audio")
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30, displaySurface: 'browser' },
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      });
      streamRef.current = stream;
      const hasAudio = stream.getAudioTracks().length > 0;

      // Detect supported mime
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' : 'video/webm';
      }

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 6_000_000,
        audioBitsPerSecond: 192_000,
      });
      recordedChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-resto-demo-${new Date().toISOString().slice(0, 10)}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        cleanupRecording();
      };

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state !== 'inactive') recorder.stop();
      };

      mediaRecorderRef.current = recorder;
      setShowExportModal(false);

      // Unmute audio so it's captured in the tab stream
      initAudioGraph();
      if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
      setIsMuted(false);

      // Restart from scratch
      setElapsed(0);
      lastTickRef.current = performance.now();
      if (voiceRef.current) {
        try { voiceRef.current.currentTime = 0; } catch (_e) { /* ignore */ }
      }
      setIsPlaying(true);

      // Warn user if they forgot to share tab audio
      if (!hasAudio) {
        setExportError("⚠️ Tu n'as pas coché « Partager l'audio de l'onglet ». La vidéo sera muette. Recommence et coche la case.");
      }

      recorder.start(250);
      recordingStartRef.current = performance.now();
      setIsRecording(true);

      setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
      }, (DURATION + 0.5) * 1000);
    } catch (e) {
      console.error('Recording failed:', e);
      if (e.name === 'NotAllowedError') {
        setExportError('Tu as annulé la sélection. Réessaie et choisis "Onglet" + coche "Partager l\'audio".');
      } else {
        setExportError("Impossible de démarrer l'export. Utilise Chrome/Edge récent.");
      }
      cleanupRecording();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      cleanupRecording();
    }
  };

  const togglePlay = () => {
    initAudioGraph();
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
    setIsPlaying((p) => !p);
  };
  const restart = () => {
    setElapsed(0);
    lastTickRef.current = performance.now();
    if (voiceRef.current) {
      try { voiceRef.current.currentTime = 0; } catch (_e) { /* ignore */ }
    }
  };
  const toggleMute = () => {
    initAudioGraph();
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
    setIsMuted((m) => !m);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    // Detect mobile / iOS — Element.requestFullscreen does NOT work reliably on iOS Safari
    // for <div> elements (only <video>), so we fall back to a CSS-based pseudo-fullscreen.
    const isTouchOrIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)) ||
      !el.requestFullscreen;

    if (isTouchOrIOS) {
      setPseudoFullscreen((prev) => !prev);
      return;
    }
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    }
  };

  // Lock body scroll while in pseudo-fullscreen (mobile)
  useEffect(() => {
    if (pseudoFullscreen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e) => { if (e.key === 'Escape') setPseudoFullscreen(false); };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = prevOverflow;
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [pseudoFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  const ACT_LABELS = {
    1: 'I · Le patron prisonnier',
    2: 'II · L\'OS du resto',
    3: 'III · Le management',
    4: 'IV · Le patron PDG',
  };

  const minutes = Math.floor(elapsed / 60);
  const seconds = Math.floor(elapsed % 60);
  const totalMinutes = Math.floor(DURATION / 60);
  const totalSeconds = DURATION % 60;

  // Trigger screen shake on cinematic impact moments
  const shakeEvent = useScreenShake(elapsed, activeScene);

  return (
    <motion.div
      ref={containerRef}
      className={`relative w-full mx-auto rounded-3xl overflow-hidden ${
        isFullscreen || pseudoFullscreen ? '' : 'aspect-[9/16] sm:aspect-video'
      }`}
      animate={shakeEvent ? {
        x: [0, -shakeEvent.intensity, shakeEvent.intensity, -shakeEvent.intensity * 0.6, shakeEvent.intensity * 0.6, 0],
        y: [0, shakeEvent.intensity * 0.5, -shakeEvent.intensity * 0.5, shakeEvent.intensity * 0.3, 0],
      } : { x: 0, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      style={{
        // Pseudo-fullscreen (mobile/iOS fallback) — covers viewport
        position: pseudoFullscreen ? 'fixed' : 'relative',
        top: pseudoFullscreen ? 0 : 'auto',
        left: pseudoFullscreen ? 0 : 'auto',
        right: pseudoFullscreen ? 0 : 'auto',
        bottom: pseudoFullscreen ? 0 : 'auto',
        zIndex: pseudoFullscreen ? 9999 : 'auto',
        maxWidth: (isFullscreen || pseudoFullscreen) ? '100vw' : 1100,
        width: (isFullscreen || pseudoFullscreen) ? '100vw' : '100%',
        height: (isFullscreen || pseudoFullscreen) ? '100vh' : 'auto',
        borderRadius: (isFullscreen || pseudoFullscreen) ? 0 : '1.5rem',
        background: 'radial-gradient(ellipse at 50% 40%, #1a0e0e 0%, #0a0505 70%, #050202 100%)',
        border: (isFullscreen || pseudoFullscreen) ? 'none' : '1px solid rgba(240,90,26,0.15)',
        boxShadow: (isFullscreen || pseudoFullscreen) ? 'none' : '0 40px 160px rgba(0,0,0,0.7), 0 0 0 1px rgba(240,90,26,0.06) inset',
      }}
      data-testid="app-demo-video"
    >
      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' seed='42'/%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      {/* Warm ambient halos · slow cinematic drift */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
        }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        style={{
          background:
            'radial-gradient(circle at 20% 30%, rgba(240,90,26,0.16) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(239,68,68,0.10) 0%, transparent 50%)',
          backgroundSize: '200% 200%',
        }}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={activeScene.id}
          className="absolute left-0 right-0 top-20 bottom-16 sm:top-20 sm:bottom-16 overflow-hidden"
        >
          {renderScene(activeScene.id)}
        </motion.div>
      </AnimatePresence>

      {/* CINEMATIC LAYER — number bursts, cash bursts, flashes, lens flares */}
      <CinematicLayer elapsed={elapsed} activeScene={activeScene} />

      {/* VIGNETTE pulse — synced with kicks */}
      <motion.div
        className="absolute inset-0 pointer-events-none rounded-3xl"
        animate={{ opacity: [0.0, 0.25, 0.0, 0.20, 0.0, 0.22, 0.0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          boxShadow: 'inset 0 0 180px rgba(0,0,0,0.85), inset 0 0 60px rgba(0,0,0,0.5)',
        }}
      />

      {/* Bottom progress */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/8">
        <motion.div
          className="h-full"
          style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #F05A1A, #FF8050)', boxShadow: '0 0 16px rgba(240,90,26,0.85)' }}
        />
      </div>

      {/* Chapter dots */}
      <div className="absolute bottom-3 left-3 right-3 flex gap-[3px]">
        {SCENES.map((s) => {
          const isActive = activeScene.id === s.id;
          return (
            <button
              key={s.id}
              onClick={() => {
                setElapsed(s.start);
                lastTickRef.current = performance.now();
              }}
              className="flex-1 h-[3px] rounded-full transition-all"
              style={{
                background: isActive ? '#F05A1A' : 'rgba(255,255,255,0.14)',
                boxShadow: isActive ? '0 0 10px rgba(240,90,26,0.7)' : 'none',
              }}
              aria-label={`Scène ${s.id}`}
              data-testid={`scene-chapter-${s.id}`}
            />
          );
        })}
      </div>

      <div className="absolute top-3 right-3 flex gap-2">
        {!isRecording && (
          <button
            onClick={() => { setExportError(''); setShowExportModal(true); }}
            className="w-9 h-9 sm:w-auto sm:h-9 sm:px-3 rounded-full bg-black/50 backdrop-blur border border-white/10 flex items-center justify-center sm:gap-1.5 text-white hover:bg-black/70 transition-colors text-[11px] font-bold"
            data-testid="video-export-btn"
            title={`Télécharger la vidéo (.webm, ${DURATION}s)`}
            aria-label="Télécharger la vidéo"
          >
            <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Exporter</span>
          </button>
        )}
        {isRecording && (
          <button
            onClick={stopRecording}
            className="px-3 h-9 rounded-full backdrop-blur flex items-center gap-2 text-white text-[11px] font-bold animate-pulse"
            style={{ background: 'rgba(220,38,38,0.85)', border: '1px solid rgba(255,255,255,0.25)' }}
            data-testid="video-stop-recording"
          >
            <span className="w-2 h-2 rounded-full bg-white" />
            REC {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(Math.floor(recordingTime % 60)).padStart(2, '0')}
            <Square className="w-3 h-3 fill-current" />
          </button>
        )}
        <button
          onClick={toggleFullscreen}
          className="w-9 h-9 rounded-full backdrop-blur border flex items-center justify-center transition-colors"
          style={
            (isFullscreen || pseudoFullscreen)
              ? { background: 'rgba(240,90,26,0.20)', borderColor: 'rgba(240,90,26,0.45)', color: '#FFB689' }
              : { background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(255,255,255,0.10)', color: '#fff' }
          }
          data-testid="video-fullscreen-toggle"
          title={(isFullscreen || pseudoFullscreen) ? 'Quitter le plein écran' : 'Mode cinéma plein écran'}
        >
          {(isFullscreen || pseudoFullscreen) ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
        <button
          onClick={toggleMute}
          className="w-9 h-9 rounded-full backdrop-blur border flex items-center justify-center transition-colors relative"
          style={
            isMuted
              ? { background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(255,255,255,0.10)', color: '#fff' }
              : { background: 'rgba(240,90,26,0.20)', borderColor: 'rgba(240,90,26,0.45)', color: '#FFB689', boxShadow: '0 0 14px rgba(240,90,26,0.35)' }
          }
          data-testid="video-mute-toggle"
          title={isMuted ? 'Activer la voix off + musique' : 'Couper le son'}
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          {isMuted && (
            <span className="absolute -top-1 -right-1 px-1 h-3.5 rounded-full bg-orange-500 text-white text-[8px] font-black flex items-center" style={{ letterSpacing: '0.05em' }}>
              SON
            </span>
          )}
        </button>
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          data-testid="video-play-pause"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          onClick={restart}
          className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          data-testid="video-restart"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Hidden audio element — voice-over */}
      <audio
        ref={voiceRef}
        src="/audio/voiceover.mp3"
        preload="auto"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
        data-testid="voiceover-audio"
      />

      {/* Export Modal */}
      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-30 flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="rounded-2xl p-5 sm:p-7 max-w-md w-full text-white"
              style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #0a0505 100%)', border: '1px solid rgba(240,90,26,0.3)', boxShadow: '0 30px 100px rgba(0,0,0,0.8)' }}
              data-testid="export-modal"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(240,90,26,0.15)', border: '1px solid rgba(240,90,26,0.3)' }}>
                  <Download className="w-5 h-5 text-orange-400" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="font-bebas text-2xl sm:text-3xl tracking-tight leading-tight">
                    {isMobileDevice ? 'TÉLÉCHARGER LA VIDÉO' : 'EXPORTER LA VIDÉO'}
                  </p>
                  <p className="text-white/55 text-xs mt-0.5">
                    {DURATION} secondes · voix off française + musique
                    {!isMobileDevice && ' · format .webm · 6 Mbps · 30 fps'}
                  </p>
                </div>
              </div>

              {isMobileDevice ? (
                /* MOBILE: instructions for native screen recorder */
                <div className="space-y-2.5 mb-5">
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">1</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      {isiOS ? (
                        <>Active l'<strong className="text-white">Enregistrement d'écran</strong> depuis ton <strong className="text-orange-400">Centre de Contrôle</strong> (balaye depuis le coin haut-droit).</>
                      ) : (
                        <>Active l'<strong className="text-white">Enregistreur d'écran</strong> depuis les <strong className="text-orange-400">Réglages rapides</strong> (balaye depuis le haut).</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">2</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      Reviens ici, passe la vidéo en <strong className="text-orange-400">plein écran</strong> (icône ⛶) et lance la lecture. Active le <strong className="text-white">son</strong> 🔊.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">3</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      Stoppe l'enregistrement après <strong className="text-white">{DURATION}s</strong>. La vidéo est dans tes <strong className="text-green-400">Photos / Galerie</strong>.
                    </p>
                  </div>
                </div>
              ) : (
                /* DESKTOP: getDisplayMedia flow */
                <div className="space-y-2.5 mb-5">
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">1</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      Clique <strong className="text-white">"Démarrer l'enregistrement"</strong> ci-dessous.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">2</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      Dans la fenêtre du navigateur, choisis <strong className="text-orange-400">"Onglet"</strong>, sélectionne <strong className="text-white">cette page</strong>, puis <strong className="text-orange-400">coche obligatoirement "Partager l'audio de l'onglet"</strong> pour capturer la voix off + musique. Ne touche à rien pendant {DURATION}s.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">3</span>
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed">
                      Le fichier <strong className="text-green-400">audit-resto-demo.webm</strong> se télécharge automatiquement à la fin.
                    </p>
                  </div>
                </div>
              )}

              {!isMobileDevice && (
                <div className="flex items-start gap-2 mb-5 rounded-lg p-2.5" style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.25)' }}>
                  <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-white/70 leading-snug">
                    Le .webm s'ouvre dans CapCut, Premiere, iPhone Photos. Tu peux le convertir en .mp4 en 5 secondes via <strong className="text-white">cloudconvert.com</strong> si besoin.
                  </p>
                </div>
              )}
              {isMobileDevice && (
                <div className="flex items-start gap-2 mb-5 rounded-lg p-2.5" style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.25)' }}>
                  <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-white/70 leading-snug">
                    Astuce : pour la version HD prête à uploader (Reels, TikTok), bascule sur <strong className="text-white">ton ordinateur</strong> et utilise le bouton Exporter — tu auras un .webm direct.
                  </p>
                </div>
              )}

              {exportError && (
                <div className="mb-4 rounded-lg p-2.5 text-xs text-red-300" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }}>
                  {exportError}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/65 text-xs font-bold hover:bg-white/10 transition-colors"
                  data-testid="export-cancel"
                >
                  {isMobileDevice ? 'Compris' : 'Annuler'}
                </button>
                {!isMobileDevice && (
                  <button
                    onClick={startRecording}
                    className="flex-[2] py-2.5 rounded-xl bg-orange-500 text-white text-xs font-black flex items-center justify-center gap-1.5 hover:bg-orange-600 transition-colors"
                    style={{ boxShadow: '0 6px 18px rgba(240,90,26,0.4)' }}
                    data-testid="export-start"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Démarrer l'enregistrement
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute top-3 left-3 flex flex-col gap-1 max-w-[60%] sm:max-w-none">
        <div className="px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10 text-white text-[10px] font-mono tabular-nums whitespace-nowrap">
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')} / {String(totalMinutes).padStart(2, '0')}:{String(totalSeconds).padStart(2, '0')}
        </div>
        <div className="px-2.5 py-1 rounded-full backdrop-blur text-[10px] font-bold uppercase tracking-[3px] truncate" style={{ background: 'rgba(240,90,26,0.15)', border: '1px solid rgba(240,90,26,0.3)', color: '#F05A1A' }}>
          <span className="sm:hidden">Acte {['', 'I', 'II', 'III', 'IV'][activeScene.act] || ''}</span>
          <span className="hidden sm:inline">Acte {ACT_LABELS[activeScene.act]}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default AppDemoVideo;
